import jsPDF from "jspdf"

const API_HOST = (process.env.REACT_APP_API_URL).replace(/\/$/, "")

// helper: fetch remote/local image as data URL for jsPDF
const toDataUrl = (src) =>
  new Promise((resolve, reject) => {
    try {
      const img = new Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        const canvas = document.createElement("canvas")
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL("image/jpeg", 0.9))
      }
      img.onerror = reject
      img.src = src
    } catch (e) {
      reject(e)
    }
  })

const getEventImageUrl = (eventImage) => {
  if (!eventImage) return null
  if (typeof eventImage !== "string") return null
  if (eventImage.startsWith("http://") || eventImage.startsWith("https://")) return eventImage
  const relativePath = eventImage.startsWith("/") ? eventImage : `/${eventImage}`
  return `${API_HOST}${relativePath}`
}

export async function generateTicketPDF({ ticket, qrCodeUrl, barcodeUrl }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()

  // Ticket card size (vertical, centered on page)
  const cardW = Math.min(160, pageW - 20)
  const cardH = Math.min(260, pageH - 20)
  const cardX = (pageW - cardW) / 2
  const cardY = (pageH - cardH) / 2

  // Light grey shadow
  doc.setFillColor(230, 230, 230)
  doc.roundedRect(cardX + 2.2, cardY + 2.6, cardW, cardH, 6, 6, "F")

  // Yellow rounded border (simulate with concentric rects)
  const steps = 8
  for (let i = 0; i < steps; i++) {
    doc.setDrawColor(239, 176, 52) // #EFB034
    doc.setLineWidth(1.6)
    doc.roundedRect(cardX + 1 + i * 0.35, cardY + 1 + i * 0.35, cardW - 2 - i * 0.7, cardH - 2 - i * 0.7, 10, 10, "S")
  }

  // White card background
  doc.setFillColor(255, 255, 255)
  doc.roundedRect(cardX + 4, cardY + 4, cardW - 8, cardH - 8, 8, 8, "F")

  // Pull cover image as faint watermark
  try {
    const imgUrl = getEventImageUrl(ticket?.eventImage)
    if (imgUrl) {
      const dataUrl = await toDataUrl(imgUrl)
      const imgW = cardW - 16
      const imgH = imgW * 0.35
      doc.addImage(dataUrl, "JPEG", cardX + 8, cardY + 36, imgW, imgH, undefined, "FAST")
      doc.setFillColor(255, 255, 255)
      doc.setDrawColor(255, 255, 255)
      doc.setGState(new doc.GState({ opacity: 0.82 }))
      doc.rect(cardX + 8, cardY + 36, imgW, imgH, "F")
      doc.setGState(new doc.GState({ opacity: 1 }))
    }
  } catch {}

  const innerPadX = cardX + 14

  // Header text
  doc.setTextColor(17, 24, 39)
  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.text("E-TICKET", innerPadX, cardY + 16)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(75, 85, 99)
  doc.text(`ID: ${ticket?.ticket_number || ticket?.id || ""}`, innerPadX, cardY + 21)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  doc.setTextColor(75, 85, 99)
  doc.text(`ID: ${ticket?.ticket_number || ticket?.id || ""}`, innerPadX, cardY + 21)

  // Event name box
  const name = ticket?.eventName || "Event"
  doc.setFont("helvetica", "bold")
  doc.setFontSize(18)
  const nameMaxW = cardW - 28
  const nameLines = doc.splitTextToSize(name, nameMaxW)
  const nameBoxH = nameLines.length * 7 + 6
  const nameBoxY = cardY + 36 + (6 - nameBoxH) / 2
  doc.setFillColor(245, 247, 250)
  doc.roundedRect(innerPadX - 6, nameBoxY, cardW - 16, nameBoxH, 3, 3, "F")
  doc.setTextColor(31, 41, 55)
  let ty = nameBoxY + nameBoxH / 2 - (nameLines.length * 7) / 2 + 6
  nameLines.forEach((line) => {
    doc.text(line, innerPadX, ty)
    ty += 7
  })

  // Event details section
  let dy = cardY + 55 + (nameLines.length - 1) * 7
  
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(31, 41, 55)

  // Handle location text wrapping
  const locationText = ticket?.eventLocation || "Location TBA"
  const locationLines = doc.splitTextToSize(locationText, cardW - 28)
  const locationBoxH = 6 + 6 + 8 + (locationLines.length - 1) * 5
  
  // Gray background for event details
  doc.setFillColor(242, 244, 247)
  doc.roundedRect(innerPadX - 6, dy - 6, cardW - 16, locationBoxH, 3, 3, "F")
  
  const eventDateText = ticket?.eventDate
    ? new Date(ticket.eventDate).toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Date TBA"
  doc.text(`Date: ${eventDateText}`, innerPadX, dy)
  dy += 6
  
  // Handle location with text wrapping
  doc.setFont("helvetica", "bold")
  doc.setTextColor(17, 24, 39)
  doc.text("Location", innerPadX, dy)
  dy += 5
  doc.setFont("helvetica", "normal")
  doc.setTextColor(31, 41, 55)
  doc.setFontSize(11)
  locationLines.forEach((line) => {
    doc.text(line, innerPadX, dy)
    dy += 5
  })
  
  // Attendee Details below
  let attendeeX = innerPadX
  let attendeeDy = dy + 8
  
  // Gray background for attendee details
  let attendeeBoxH = 6 + 6 + 8
  if (ticket?.delegate?.email) attendeeBoxH += 6
  if (ticket?.delegate?.phone) attendeeBoxH += 6
  
  doc.setFillColor(242, 244, 247)
  doc.roundedRect(innerPadX - 6, attendeeDy - 6, cardW - 16, attendeeBoxH, 3, 3, "F")
  
  doc.setFont("helvetica", "bold")
  doc.setTextColor(17, 24, 39)
  doc.text("Attendee", attendeeX, attendeeDy)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(31, 41, 55)
  attendeeDy += 6
  const attendeeName = ticket?.delegate
    ? `${ticket.delegate.name || ""} ${ticket.delegate.surname || ""}`.trim() || "Guest Attendee"
    : "Guest Attendee"
  doc.text(`${attendeeName}`, attendeeX, attendeeDy)
  attendeeDy += 6
  if (ticket?.delegate?.email) {
    doc.text(`${ticket.delegate.email}`, attendeeX, attendeeDy)
    attendeeDy += 6
  }
  if (ticket?.delegate?.phone) {
    doc.text(`${ticket.delegate.phone}`, attendeeX, attendeeDy)
    attendeeDy += 6
  }

  // QR Code above barcode (moved higher up to leave room for the notice below)
  let bottomY = Math.max(attendeeDy + 6, cardY + cardH - 132)
  const centerX = cardX + cardW / 2
  if (qrCodeUrl) {
    const qrSize = 52
    const qrX = centerX - qrSize / 2
    const qrY = bottomY
    doc.setFillColor(255, 255, 255)
    doc.roundedRect(qrX - 6, qrY - 6, qrSize + 12, qrSize + 12, 4, 4, "F")
    doc.addImage(qrCodeUrl, "PNG", qrX, qrY, qrSize, qrSize, undefined, "FAST")
    bottomY = qrY + qrSize + 5
  }

  // Barcode below QR code
  if (barcodeUrl) {
    const bw = 90 // mm
    const bh = 26 // mm
    const bx = centerX - bw / 2
    const barcodeY = bottomY
    doc.setFillColor(255, 255, 255)
    doc.roundedRect(bx - 6, barcodeY - 6, bw + 12, bh + 16, 3, 3, "F")
    doc.addImage(barcodeUrl, "PNG", bx, barcodeY, bw, bh, undefined, "FAST")
    bottomY = barcodeY + bh + 10

    doc.setTextColor(30, 30, 30)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8)
    const codeText = (ticket?.ticket_number || ticket?.id || "").toString()
    doc.text(codeText, centerX, bottomY, { align: "center" })
    bottomY += 6
  }

  // Terms & conditions / organiser contact notice below the barcode
  const noticeMaxW = cardW - 28
  let noticeY = bottomY + 4
  doc.setDrawColor(226, 232, 240)
  doc.setLineWidth(0.3)
  doc.line(innerPadX - 6, noticeY - 2, innerPadX - 6 + noticeMaxW, noticeY - 2)
  noticeY += 4

  doc.setTextColor(75, 85, 99)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(7.5)
  const noticeLines = doc.splitTextToSize(
    "All terms and conditions of the platform apply. For any queries please contact the event organiser:",
    noticeMaxW
  )
  noticeLines.forEach((line) => {
    doc.text(line, centerX, noticeY, { align: "center" })
    noticeY += 3.6
  })

  const organiserName = ticket?.organiserName || ticket?.organiser || ""
  const organiserEmail = ticket?.organiserEmail || ""
  if (organiserName || organiserEmail) {
    noticeY += 1
    doc.setFont("helvetica", "normal")
    doc.setTextColor(31, 41, 55)
    doc.setFontSize(8)
    const contactText = [organiserName, organiserEmail].filter(Boolean).join(" — ")
    doc.splitTextToSize(contactText, noticeMaxW).forEach((line) => {
      doc.text(line, centerX, noticeY, { align: "center" })
      noticeY += 4
    })
  }

  // Bottom line (no generated date)
  const cardBottomY = cardY + cardH - 6
  doc.setTextColor(100, 116, 139)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(8)
  doc.text("Powered by BuzzTicket", cardX + 10, cardBottomY)

  // Save
  const fileNameName = (ticket?.delegate?.name || ticket?.delegate?.surname || "Attendee")
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
  const fileNameEvent = (ticket.eventName || "Event").replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "")
  doc.save(`${fileNameEvent}_${fileNameName}_Ticket.pdf`)
}
